import { useCallback, useMemo, useRef, useState } from 'react'

import {
  GetServerSidePropsContext,
  InferGetServerSidePropsType,
  NextPage,
} from 'next'
import Head from 'next/head'
import Image from 'next/image'
import Link from 'next/link'
import { useRouter } from 'next/router'

import { withIronSessionSsr } from 'iron-session/next'
import invariant from 'tiny-invariant'

import { isSuperFetchError } from '~/api-client'
import {
  useFoodMutation,
  useFoodSWR,
  useFoodSWRWithErrors,
} from '~/api-client/food_api'
import {
  chatGetChatSpace,
  chatGetOrCreateChatSpace,
} from '~/api-client/food_api/chat'
import {
  useUserOwnedGifts,
  useUserOwnedPoints,
  userGetProfile,
  userUserMemo,
} from '~/api-client/food_api/user'
import { useUserInvitedUser } from '~/api-client/food_api/user/invited_user'
import { User } from '~/api-definition/definition'
import { UserMemoResponse, UserResponse } from '~/api-definition/response'
import { Button } from '~/components/action/Button'
import { Modal } from '~/components/action/Modal'
import { AppImage } from '~/components/data-display/AppImage'
import { NumberLoader } from '~/components/data-display/Loading'
import { UnreadBadge } from '~/components/data-display/UnreadBadge'
import { TextArea } from '~/components/data-input/TextField'
import { ShowOnly } from '~/components/food_app/ShowOnly'
import { ScheduledDinnerList } from '~/components/food_app/dinner/ScheduledDinnerList'
import { IntroductionLetterListItem } from '~/components/food_app/user/IntroductionLetterListItem'
import { SocialLinks } from '~/components/food_app/user/SocialLinks'
import { UserFollowLargeButton } from '~/components/food_app/user/UserFollowLargeButton'
import { UserWarningStatusIconButton } from '~/components/food_app/user/UserWarningStatus'
import { GoogleLocalGuide } from '~/components/google-local-guide'
import {
  BlacklistIcon,
  ChevronLeftIcon,
  EditIcon,
  GiftIcon,
  InfoIcon,
  MenuIcon,
  MessageIcon,
  NotificationIcon,
  PlusIcon,
} from '~/components/icons'
import { Container, ContainerInset } from '~/components/layout/Container'
import { Footer } from '~/components/layout/Footer'
import { Header } from '~/components/layout/Header'
import { goToChatGiftsPage, haveDmChatSpace } from '~/libs/chat'
import {
  formatNumberWithJapaneseStyleOpt,
  numberWithCommasOpt,
} from '~/libs/number'
import {
  userAlcoholAmountTypeLabel,
  userAttributesLabel,
  userProfileIcon,
  userProfileIconOpt,
} from '~/libs/user'
import {
  useApplication,
  useApplicationMode,
} from '~/provider/ApplicationProvider'
import { useUnreadBadge } from '~/provider/UnreadBadgeProvider'
import { sessionOptions } from '~/session'

type StatsProps = {
  isMe: boolean
  userName: string
  userAttendedDinners?: number
  userOrganizedDinners?: number
  followerCount?: number
  followingCount?: number
}
const Stats = (props: StatsProps) => {
  return (
    <>
      <div className="flex flex-col items-center border-r border-r-[#DEDEDE] basis-1/4 text-center">
        <Link
          href={
            props.isMe
              ? `/account/dinners?default_disclosure=attended`
              : `/foodies/${props.userName}/dinners?default_disclosure=attended`
          }
        >
          <div className="text-lg font-bold leading-tight">
            {formatNumberWithJapaneseStyleOpt(props.userAttendedDinners) ?? (
              <NumberLoader />
            )}
          </div>
          <div className="text-[9px]">参加</div>
        </Link>
      </div>
      <div className="flex flex-col items-center border-r border-r-[#DEDEDE] basis-1/4 text-center">
        <Link
          href={
            props.isMe
              ? `/account/dinners?default_disclosure=organized`
              : `/foodies/${props.userName}/dinners?default_disclosure=organized`
          }
        >
          <div className="text-lg font-bold leading-tight">
            {formatNumberWithJapaneseStyleOpt(props.userOrganizedDinners) ?? (
              <NumberLoader />
            )}
          </div>
          <div className="text-[9px]">幹事</div>
        </Link>
      </div>
      <div className="flex flex-col items-center border-r border-r-[#DEDEDE] basis-1/4 text-center">
        <Link
          href={
            props.isMe
              ? `/account/friends/follower`
              : `/foodies/${props.userName}/follower`
          }
        >
          <div className="text-lg font-bold leading-tight">
            {formatNumberWithJapaneseStyleOpt(props.followerCount) ?? (
              <NumberLoader />
            )}
          </div>
          <div className="text-[9px]">フォロワー</div>
        </Link>
      </div>
      <div className="flex flex-col items-center text-center basis-1/4">
        <Link
          href={
            props.isMe
              ? '/account/friends/follow'
              : `/foodies/${props.userName}/follow`
          }
        >
          <div className="text-lg font-bold leading-tight">
            {formatNumberWithJapaneseStyleOpt(props.followingCount) ?? (
              <NumberLoader />
            )}
          </div>
          <div className="text-[9px]">フォロー中</div>
        </Link>
      </div>
    </>
  )
}

const Page: NextPage<InferGetServerSidePropsType<typeof getServerSideProps>> = (
  props
) => {
  const router = useRouter()
  const app = useApplication()

  const applicationMode = useApplicationMode()

  const invalidateGetServerSideProps = useCallback(async () => {
    await router.replace(router.asPath)
  }, [router])
  const userOrganizedDinners = useFoodSWR('/user/dinners', {
    userId: props.userResponse.user.userId,
    passedScheduledAt: true,
    memberFilter: 'organized',
  })
  const userAttendedDinners = useFoodSWR('/user/dinners', {
    userId: props.userResponse.user.userId,
    passedScheduledAt: true,
    memberFilter: 'attended',
  })

  const userOwnedGifts = useUserOwnedGifts({
    searchParams: {
      user_id: props.userResponse.user.userId,
    },
  })

  const invitedFrom = useUserInvitedUser({
    searchParams: {
      user_id: props.userResponse.user.userId,
    },
  })

  // useUserVisitBadges()
  // useUserMetals()

  const memoInputRef = useRef<HTMLTextAreaElement | null>(null)

  const userUnreadBadgeList = useUnreadBadge()
  const userOwnedPoints = useUserOwnedPoints()
  const [messageModal, setMessageModal] = useState(false)
  const [messageGiftModal, setMessageGiftModal] = useState(false)
  const [muteModal, setMuteModal] = useState(false)
  const [unmuteModal, setUnmuteModal] = useState(false)

  const muteUser = useFoodMutation('/user/mute_user', {
    onSuccess: () => {
      invalidateGetServerSideProps()
    },
  })

  const unmuteUser = useFoodMutation('/user/unmute_user', {
    onSuccess: () => {
      invalidateGetServerSideProps()
    },
  })

  const restaurantFollowingList = useFoodSWR(
    '/user/restaurant_following_list',
    {
      userId: props.userResponse.user.userId,
    }
  )
  const restaurantFollowingListWishRestaurants = useMemo(() => {
    return restaurantFollowingList.data?.followingList.wishRestaurants
      .filter((r) => r.restaurantName != null && r.restaurantId != null)
      .map((r) => ({
        restaurantName: String(r.restaurantName),
        restaurantId: String(r.restaurantId),
      }))
  }, [restaurantFollowingList.data?.followingList.wishRestaurants])

  const pushChatSpace = async () => {
    if (props.accessToken !== undefined) {
      const chatSpace = await chatGetOrCreateChatSpace({
        accessToken: props.accessToken,
        pairUserId: props.userResponse.user.userId,
      })
      await router.push(`/chat_spaces/${chatSpace.space.chatSpaceId}`)
    }
  }

  // 一週間以内に有効期限が切れるポイントがあればアラートを表示
  const showPointExpirationAlert = useMemo(() => {
    const e = userOwnedPoints.data?.ownedPoints.map((point) => point.expiredAt)
    if (e == null) return null
    return Math.min(...e) * 1000 <= Date.now() + 7 * 24 * 60 * 60 * 1000
  }, [userOwnedPoints.data])

  return (
    <>
      <Head>
        <title>{props.isMe ? 'マイページ' : 'プロフィール'}</title>
      </Head>
      <Header>
        <div className="w-20">
          {!props.isMe && (
            <ChevronLeftIcon fill="#fff" onClick={() => router.back()} />
          )}
        </div>
        {props.isMe ? 'マイページ' : 'プロフィール'}
        <div className="flex justify-end w-20 gap-5">
          {props.isMe && (
            <>
              <Link
                href="/account/notifications"
                className="relative"
                title="お知らせ"
              >
                <NotificationIcon className="w-[18px] h-[21.51px] fill-white" />

                <div className="absolute top-[-30%] right-[-70%]">
                  <UnreadBadge
                    count={
                      userUnreadBadgeList.response != null
                        ? userUnreadBadgeList.response.unreadBadgeList
                            .serviceNotice +
                          userUnreadBadgeList.response.unreadBadgeList
                            .userNotification
                        : 0
                    }
                  />
                </div>
              </Link>
              <Link href="/account">
                <MenuIcon className="w-[21px] h-[21px] fill-white" />
              </Link>
            </>
          )}
        </div>
      </Header>

      {/* TODO: DmNavigationProvider に置き換え */}
      <Modal
        isOpen={messageModal}
        setIsOpen={setMessageModal}
        title={'メッセージルームを作成しますか？'}
        actionButtons={
          <div className="grid grid-cols-2 mt-8 gap-x-4">
            <Button
              type="button"
              variant="functionWeak"
              shrink
              onClick={async (e) => {
                e.stopPropagation()

                setMessageModal(false)
              }}
            >
              キャンセル
            </Button>
            <Button
              type="button"
              variant="primary"
              shrink
              onClick={async (e) => {
                e.stopPropagation()

                pushChatSpace()
                setMessageModal(false)
              }}
            >
              作成する
            </Button>
          </div>
        }
      />

      {/* TODO: DmNavigationProvider に置き換え */}
      {/*       ただし、「ギフトを贈るために～」のテキストに対応させるために、DmNavigationProvider の拡張は必要 */}
      <Modal
        isOpen={messageGiftModal}
        setIsOpen={setMessageGiftModal}
        title={'メッセージルームを作成しますか？'}
        body={
          <div className="text-center">
            <p>ギフトを贈るために</p>
            <p>メッセージルームが必要となります。</p>
          </div>
        }
        actionButtons={
          <div className="grid grid-cols-2 mt-8 gap-x-4">
            <Button
              type="button"
              variant="functionWeak"
              shrink
              onClick={async (e) => {
                e.stopPropagation()

                setMessageGiftModal(false)
              }}
            >
              キャンセル
            </Button>
            <Button
              type="button"
              variant="primary"
              shrink
              onClick={async (e) => {
                e.stopPropagation()

                if (props.accessToken == null) return
                const chatSpace = await chatGetOrCreateChatSpace({
                  accessToken: props.accessToken,
                  pairUserId: props.userResponse.user.userId,
                })
                goToChatGiftsPage(app, router, chatSpace.space)
                setMessageGiftModal(false)
              }}
            >
              作成する
            </Button>
          </div>
        }
      />

      <Modal
        isOpen={muteModal}
        setIsOpen={setMuteModal}
        title={`${props.userResponse.user.displayName}さんをブロックしますか？`}
        actionButtons={
          <div className="grid grid-cols-2 mt-8 gap-x-4">
            <Button
              type="button"
              variant="functionWeak"
              shrink
              onClick={async (e) => {
                e.stopPropagation()
                setMuteModal(false)
              }}
            >
              キャンセル
            </Button>
            <Button
              type="button"
              variant="primary"
              shrink
              onClick={async (e) => {
                e.stopPropagation()

                muteUser({
                  userId: props.userResponse.user.userId,
                })
                setMuteModal(false)
              }}
            >
              ブロック
            </Button>
          </div>
        }
      />

      <Modal
        isOpen={unmuteModal}
        setIsOpen={setUnmuteModal}
        title={`${props.userResponse.user.displayName}さんのブロックを解除しますか？`}
        actionButtons={
          <div className="grid grid-cols-2 mt-8 gap-x-4">
            <Button
              type="button"
              variant="functionWeak"
              shrink
              onClick={async (e) => {
                e.stopPropagation()
                setUnmuteModal(false)
              }}
            >
              キャンセル
            </Button>
            <Button
              type="button"
              variant="primary"
              shrink
              onClick={async (e) => {
                e.stopPropagation()

                unmuteUser({
                  userId: props.userResponse.user.userId,
                })
                setUnmuteModal(false)
              }}
            >
              解除
            </Button>
          </div>
        }
      />

      {props.userResponse.user.muted && (
        <div className="text-sm text-white bg-secondary">
          <Container>
            <div className="py-2 mx-4 md:mx-0">
              <div className="flex justify-between">
                ブロックしているユーザです
                <button
                  type="button"
                  onClick={() => {
                    setUnmuteModal(true)
                  }}
                >
                  解除
                </button>
              </div>
            </div>
          </Container>
        </div>
      )}

      <Container>
        <ContainerInset>
          <div className="flex gap-4">
            <div className="min-w-[100px]">
              <AppImage
                variant={{ type: 'profile', user: props.userResponse.user }}
                rounded="full"
                width={100}
                height={100}
                alt={`${props.userResponse.user.displayName}さんのプロフィール画像`}
                src={userProfileIcon(props.userResponse.user)}
              />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between">
                <div className="min-w-0">
                  <div className="font-bold flex gap-x-1 items-center">
                    {props.userResponse.user.displayName}
                    {props.userResponse.user.warningStatus != null && (
                      <UserWarningStatusIconButton
                        userWarningStatus={
                          props.userResponse.user.warningStatus
                        }
                      />
                    )}
                  </div>
                  <div className="text-xs">
                    @{props.userResponse.user.userName}
                  </div>
                  <div className="text-xs">
                    {userAttributesLabel(props.userResponse.user, {
                      omitParen: true,
                    })}
                  </div>
                </div>{' '}
                {!props.isMe && !props.userResponse.user.muted && (
                  <button
                    type="button"
                    onClick={() => {
                      setMuteModal(true)
                    }}
                  >
                    <div className="flex items-center justify-center w-6 h-6 rounded-full bg-black">
                      <BlacklistIcon className="fill-white w-3 h-3 stroke-white stroke-[0.5]" />
                    </div>
                  </button>
                )}
              </div>
              <div className="flex items-center justify-between mt-3">
                <Stats
                  isMe={props.isMe}
                  userName={props.userResponse.user.userName}
                  userAttendedDinners={userAttendedDinners.data?.paging.all}
                  userOrganizedDinners={userOrganizedDinners.data?.paging.all}
                  followerCount={props.userResponse.user.followerCount}
                  followingCount={props.userResponse.user.followingCount}
                />
              </div>
            </div>
          </div>

          {!props.isMe && (
            <div className="flex justify-center gap-x-4 mt-4">
              <UserFollowLargeButton
                user={props.userResponse.user}
                onChangeFollowingStatus={invalidateGetServerSideProps}
              />
              <Button
                type="button"
                className="text-sm"
                variant="functionWeak"
                onClick={async () => {
                  if (props.accessToken == null) return
                  const isDmChatSpace = await haveDmChatSpace(
                    props.accessToken,
                    props.userResponse.user.userId
                  )
                  if (!isDmChatSpace) {
                    setMessageModal(true)
                  } else {
                    pushChatSpace()
                  }
                }}
              >
                <MessageIcon className="w-4 h-4 fill-black" />
                メッセージ
              </Button>
            </div>
          )}

          <div className="whitespace-pre-wrap break-words text-[13px] mt-3">
            {props.userResponse.user.biography}
          </div>

          {props.userResponse.user.showGoogleLocalGuidesLevel &&
            props.userResponse.user.googleLocalGuidesLevel != null && (
              <div className="text-sm mt-2 flex gap-0.5">
                Googleローカルガイド：
                <GoogleLocalGuide
                  level={props.userResponse.user.googleLocalGuidesLevel}
                />
              </div>
            )}

          <div className="flex flex-wrap items-center my-3 gap-x-4">
            {SocialLinks({ user: props.userResponse.user })}
          </div>
          {invitedFrom.data?.user !== undefined ? (
            <div className="text-[12px] text-[#999] flex gap-x-2 items-center">
              <Link href={`/foodies/${invitedFrom.data?.user.userName}`}>
                <AppImage
                  variant={{ type: 'profile', user: invitedFrom.data?.user }}
                  rounded="full"
                  width={24}
                  height={24}
                  className="rounded-full"
                  alt={`招待者さんのプロフィール画像`}
                  src={userProfileIconOpt(invitedFrom.data?.user)}
                />
              </Link>
              <div>
                <Link href={`/foodies/${invitedFrom.data?.user.userName}`}>
                  <span className="mr-1">
                    {invitedFrom.data?.user.userName}
                  </span>
                </Link>
                さんの紹介で登録
              </div>
            </div>
          ) : (
            <></>
          )}
        </ContainerInset>
      </Container>
      {props.isMe && (
        <div className="my-5 rounded-md bg-primary-cv2">
          <Container>
            <div className="px-[15px] py-5 md:px-0">
              {applicationMode !== 'inapp' && (
                <div className="w-[320px] mx-auto">
                  <div className="flex items-center justify-between gap-y-4">
                    <div className="flex items-center gap-x-4">
                      <div className="text-sm">保有ポイント</div>
                      <div className="text-[20px] font-bold">
                        {numberWithCommasOpt(
                          userOwnedPoints.data?.availableAmount,
                          ''
                        )}
                        {userOwnedPoints.data?.availableAmount !== undefined
                          ? 'P'
                          : ''}
                      </div>
                    </div>
                  </div>
                  <div className="flex justify-center gap-4 mt-3">
                    {app.paymentType === 'web' && (
                      <Link href="/account/points/charge">
                        <Button
                          type="button"
                          variant="primary"
                          className="flex-1"
                        >
                          ポイント購入
                        </Button>
                      </Link>
                    )}
                    <Link href="/account/points/exchange">
                      <Button
                        type="button"
                        variant="primary"
                        className="bg-primary hover:bg-primary flex-1"
                      >
                        ポイント交換
                      </Button>
                    </Link>
                  </div>
                </div>
              )}
              {applicationMode === 'inapp' && (
                <>
                  <div className="flex items-center justify-between">
                    <div className="text-sm">保有ポイント</div>
                    <div className="ml-auto mr-[25px]">
                      <div className="text-[20px] font-bold">
                        {numberWithCommasOpt(
                          userOwnedPoints.data?.availableAmount,
                          ''
                        )}
                        {userOwnedPoints.data?.availableAmount !== undefined
                          ? 'P'
                          : ''}
                      </div>
                    </div>
                    <Link href="/account/points/exchange">
                      <Button
                        type="button"
                        variant="primary"
                        className="bg-primary hover:bg-primary"
                      >
                        ポイント交換
                      </Button>
                    </Link>
                  </div>
                </>
              )}
              {showPointExpirationAlert && (
                <div className="bg-white rounded px-2 py-1 flex justify-between items-center mt-3">
                  <div className="flex gap-2">
                    <InfoIcon className="fill-secondary w-5 h-5" />
                    <div className="text-sm text-secondary">
                      ポイントの有効期限が近付いています
                    </div>
                  </div>
                  <Link
                    href="/account/points"
                    className="rounded text-white bg-secondary text-sm px-2 py-1 font-bold"
                  >
                    確認
                  </Link>
                </div>
              )}
            </div>
          </Container>
        </div>
      )}
      {!props.isMe && (
        <Container>
          <ContainerInset>
            <div className="bg-[#E6E6EA] p-4 rounded-md">
              <div className="flex items-center mb-2">
                <p className="text-sm font-bold">メモ</p>
                <Link
                  href={`/foodies/${props.userResponse.user.userName}/memo`}
                  className="ml-auto"
                >
                  <EditIcon className="fill-black" />
                </Link>
              </div>

              <Link href={`/foodies/${props.userResponse.user.userName}/memo`}>
                <TextArea
                  inputProps={{
                    placeholder: 'あなたにしか見えないこのユーザーのメモです。',
                    ref: memoInputRef,
                    rows: 2,
                    readOnly: true,
                    defaultValue: props.userMemoResponse?.memo.text,
                  }}
                />
              </Link>
            </div>
          </ContainerInset>
        </Container>
      )}
      <Container>
        <ContainerInset>
          {props.isMe && (
            <div className="bg-[#EAEAEA] flex items-center justify-center gap-2 py-4 my-4">
              <div className="flex justify-center">
                <Link
                  href="/account/invitation"
                  className="flex items-center justify-center gap-2 bg-[#08092D] text-white rounded-md py-2 w-40"
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="size-6 fill-white"
                  >
                    <path d="M10.0001 20C8.48816 20.0043 6.99532 19.6622 5.6361 19C5.13865 18.758 4.66203 18.4754 4.2111 18.155L4.0741 18.055C2.83392 17.1396 1.81997 15.9522 1.1101 14.584C0.375836 13.1679 -0.00499271 11.5952 4.94229e-05 10C4.94229e-05 4.47715 4.47725 0 10.0001 0C15.5229 0 20.0001 4.47715 20.0001 10C20.0051 11.5944 19.6247 13.1664 18.8911 14.582C18.1822 15.9494 17.1697 17.1364 15.9311 18.052C15.4639 18.394 14.968 18.6951 14.4491 18.952L14.3691 18.992C13.009 19.6577 11.5144 20.0026 10.0001 20ZM10.0001 15C8.50158 14.9971 7.12776 15.834 6.4431 17.167C8.68449 18.2772 11.3157 18.2772 13.5571 17.167V17.162C12.8716 15.8305 11.4977 14.9954 10.0001 15ZM10.0001 13C12.1662 13.0028 14.1635 14.1701 15.2291 16.056L15.2441 16.043L15.2581 16.031L15.2411 16.046L15.2311 16.054C17.7601 13.8691 18.6644 10.3423 17.4987 7.21011C16.3331 4.07788 13.3432 2.00032 10.0011 2.00032C6.65901 2.00032 3.66909 4.07788 2.50345 7.21011C1.33781 10.3423 2.2421 13.8691 4.7711 16.054C5.83736 14.169 7.83446 13.0026 10.0001 13ZM10.0001 12C7.79096 12 6.0001 10.2091 6.0001 8C6.0001 5.79086 7.79096 4 10.0001 4C12.2092 4 14.0001 5.79086 14.0001 8C14.0001 9.06087 13.5787 10.0783 12.8285 10.8284C12.0784 11.5786 11.061 12 10.0001 12ZM10.0001 6C8.89553 6 8.0001 6.89543 8.0001 8C8.0001 9.10457 8.89553 10 10.0001 10C11.1047 10 12.0001 9.10457 12.0001 8C12.0001 6.89543 11.1047 6 10.0001 6Z" />
                  </svg>
                  <div className="text-sm font-semibold space-y-1">
                    <p className="text-[13px] font-bold leading-none">
                      友達を招待する
                    </p>
                    <p className="text-[10px] leading-none">Invite friends</p>
                  </div>
                </Link>
              </div>
              <div className="flex justify-center">
                <Link
                  href="https://www.foodies-reserve.com/"
                  className="flex items-center justify-center gap-2 rounded-md py-2 w-40 text-white"
                  style={{
                    background:
                      'linear-gradient(135deg, #C69E51 0%, #AD3F4C 100%)',
                  }}
                >
                  <svg
                    width="25"
                    height="24"
                    viewBox="0 0 25 24"
                    xmlns="http://www.w3.org/2000/svg"
                    className="fill-white size-6"
                  >
                    <g clipPath="url(#clip0_317_6014)">
                      <path d="M15.7343 9.66858C14.5828 9.66858 13.5646 9.91665 12.7019 10.3276C11.8652 9.92035 10.8322 9.66858 9.57702 9.66858C6.22256 9.66858 3.86037 11.6642 3.86037 14.2671C3.86037 16.7329 5.9893 18.7101 9.57702 18.7101C10.7137 18.7101 11.7282 18.4657 12.5835 18.051C13.4461 18.462 14.4976 18.7101 15.7343 18.7101C18.9962 18.7101 21.3584 16.7922 21.3584 14.1523C21.3584 11.7642 19.459 9.67228 15.7343 9.67228V9.66858ZM9.6955 18.1399C7.01119 18.1399 5.92266 15.9665 5.92266 13.9524C5.92266 11.194 7.75169 10.2351 9.41781 10.2351C10.2657 10.2351 11.0765 10.4831 11.7948 10.8756C10.6878 11.705 10.0213 12.8972 10.0213 14.2671C10.0213 15.637 10.71 16.8588 11.9281 17.6734C11.1728 17.9955 10.362 18.1436 9.6992 18.1436L9.6955 18.1399ZM14.2644 14.4448C14.2644 15.6777 13.6683 16.5552 12.8537 17.1476C11.7097 16.3294 10.9581 15.1224 10.9581 13.9561C10.9581 12.7083 11.5616 11.8345 12.3761 11.2458C13.498 12.0567 14.2681 13.2489 14.2681 14.4448H14.2644ZM15.8491 18.1399C14.979 18.1399 14.1718 17.8955 13.4609 17.5179C14.5458 16.7033 15.2011 15.5296 15.2011 14.1486C15.2011 12.7676 14.5643 11.5383 13.3425 10.7016C14.1163 10.3758 14.9382 10.2351 15.5714 10.2351C18.1187 10.2351 19.2998 12.3492 19.2998 14.4411C19.2998 17.1217 17.6114 18.1399 15.8491 18.1399Z" />
                      <path d="M12.6094 9.46495L11.1358 7.36193L12.6094 5.25891L14.0867 7.36193L12.6094 9.46495Z" />
                      <path d="M7.68878 8.53932C8.17749 8.53932 8.57367 8.14314 8.57367 7.65443C8.57367 7.16571 8.17749 6.76953 7.68878 6.76953C7.20006 6.76953 6.80388 7.16571 6.80388 7.65443C6.80388 8.14314 7.20006 8.53932 7.68878 8.53932Z" />
                      <path d="M3.86036 9.72414C4.27955 9.72414 4.61937 9.38432 4.61937 8.96513C4.61937 8.54594 4.27955 8.20612 3.86036 8.20612C3.44117 8.20612 3.10135 8.54594 3.10135 8.96513C3.10135 9.38432 3.44117 9.72414 3.86036 9.72414Z" />
                      <path d="M21.3583 9.72414C21.7775 9.72414 22.1173 9.38432 22.1173 8.96513C22.1173 8.54594 21.7775 8.20612 21.3583 8.20612C20.9391 8.20612 20.5993 8.54594 20.5993 8.96513C20.5993 9.38432 20.9391 9.72414 21.3583 9.72414Z" />
                      <path d="M17.3188 8.53932C17.8076 8.53932 18.2037 8.14314 18.2037 7.65443C18.2037 7.16571 17.8076 6.76953 17.3188 6.76953C16.8301 6.76953 16.4339 7.16571 16.4339 7.65443C16.4339 8.14314 16.8301 8.53932 17.3188 8.53932Z" />
                    </g>
                    <defs>
                      <clipPath id="clip0_317_6014">
                        <rect
                          width="20"
                          height="13.4821"
                          transform="translate(2.50009 5.25891)"
                        />
                      </clipPath>
                    </defs>
                  </svg>
                  <div className="text-sm font-semibold space-y-1">
                    <p className="text-[13px] font-bold leading-none">
                      飲食店を予約する
                    </p>
                    <p className="text-[10px] leading-none">Reservation</p>
                  </div>
                </Link>
              </div>
            </div>
          )}
          <IntroductionLettersSection
            user={props.userResponse.user}
            isMe={props.isMe}
          />
          <div className="flex flex-col px-4 py-5 mb-4 rounded-md bg-primary-cv2 gap-y-4">
            <ShowOnly foodEnvs={['sandbox']}>
              <div className="text-sm font-bold">称号</div>
              {props.titles.length === 0 ? (
                <div className="mt-1 text-sm b-2">称号はありません</div>
              ) : (
                <div className="relative">
                  <div className="grid grid-cols-5 gap-1 mt-2 md:grid-cols-10">
                    {props.titles.map((gift) => {
                      return (
                        <div
                          className="flex flex-col items-center"
                          key={gift.id}
                        >
                          <Image
                            unoptimized
                            src={gift.image}
                            width={105}
                            height={105}
                            alt="gift example"
                          />
                        </div>
                      )
                    })}
                  </div>
                  <div className="text-xs text-[#999] mt-2 text-right -mb-4 right-0">
                    もっと見る
                  </div>
                </div>
              )}
            </ShowOnly>
            <div>
              <div className="text-sm font-bold">もらったギフト</div>
              {userOwnedGifts.data?.ownedGifts.length === 0 ? (
                <div className="mt-1 text-sm b-2">ギフトはありません</div>
              ) : (
                <div className="relative">
                  <div className="grid grid-cols-5 gap-1 mt-2 md:grid-cols-10">
                    {userOwnedGifts.data?.ownedGifts.map((ownedGift) => {
                      return (
                        <div
                          className="relative flex flex-col items-center"
                          key={ownedGift.gift.giftId}
                        >
                          <Image
                            unoptimized
                            src={ownedGift.gift.imageUrl}
                            width={105}
                            height={105}
                            alt="gift example"
                          />
                          {ownedGift.ownedCount > 1 && (
                            <div className="bg-[#666] w-5 h-5 flex justify-center items-center rounded-full text-white text-xs absolute right-0 top-0 ">
                              {ownedGift.ownedCount}
                            </div>
                          )}
                        </div>
                      )
                    })}
                  </div>
                  <Link
                    href={
                      props.isMe
                        ? '/account/gifts'
                        : `/foodies/${props.userResponse.user.userName}/gifts`
                    }
                  >
                    <div className="text-xs text-[#999] mt-2 text-right -mb-4 right-0">
                      もっと見る
                    </div>
                  </Link>
                </div>
              )}
            </div>
            <ShowOnly foodEnvs={['sandbox']}>
              <div className="text-sm font-bold">訪問バッジ</div>
              {props.visits.length === 0 ? (
                <div className="mt-1 text-sm b-2">訪問バッジはありません</div>
              ) : (
                <div className="relative">
                  <div className="grid grid-cols-5 gap-1 mt-2 md:grid-cols-10">
                    {props.visits.map((gift) => {
                      return (
                        <div
                          className="relative flex flex-col items-center"
                          key={gift.id}
                        >
                          <Image
                            unoptimized
                            src={gift.image}
                            width={105}
                            height={105}
                            alt="gift example"
                          />
                          <div className="bg-[#666] w-5 h-5 flex justify-center items-center rounded-full text-white text-xs absolute right-0 top-0 ">
                            4
                          </div>
                        </div>
                      )
                    })}
                  </div>
                  <div className="text-xs text-[#999] mt-2 text-right right-0">
                    もっと見る
                  </div>
                </div>
              )}
            </ShowOnly>

            {!props.isMe && (
              <Button
                variant="primary"
                className="mx-auto px-6 flex items-center gap-2"
                onClick={async () => {
                  if (props.accessToken == null) return
                  const isDmChatSpace = await haveDmChatSpace(
                    props.accessToken,
                    props.userResponse.user.userId
                  )
                  if (!isDmChatSpace) {
                    setMessageGiftModal(true)
                  } else {
                    const chatSpace = await chatGetChatSpace({
                      accessToken: props.accessToken,
                      pairUserId: props.userResponse.user.userId,
                    })
                    goToChatGiftsPage(app, router, chatSpace.space)
                  }
                }}
              >
                <GiftIcon className="w-4 h-4 fill-white" />
                ギフトを贈る
              </Button>
            )}
          </div>

          <div className="flex flex-col gap-y-4">
            <div>
              <div className="text-sm font-bold">行きたいお店</div>
              <div className="flex items-end justify-between">
                <div className="flex flex-wrap items-center justify-start gap-2 mt-2 text-xs">
                  {restaurantFollowingListWishRestaurants?.length === 0 && (
                    <div className="mt-1 text-sm">登録がありません</div>
                  )}
                  {restaurantFollowingListWishRestaurants?.map((r, i) => {
                    return (
                      <Link
                        className="px-4 py-1 rounded-full bg-function-cv2"
                        key={r.restaurantId}
                        href={`/restaurants/${r.restaurantId}`}
                      >
                        {r.restaurantName}
                      </Link>
                    )
                  })}
                </div>
                {props.isMe && (
                  <Link href="/account/following_restaurants/wish">
                    <div className="flex items-center justify-center flex-shrink-0 w-6 h-6 rounded-full bg-function-cv2">
                      <PlusIcon className="w-2 h-2 fill-text" />
                    </div>
                  </Link>
                )}
              </div>
            </div>
            <div>
              <div className="text-sm font-bold">苦手な食材</div>
              <div className="flex items-end justify-between">
                <div className="mt-1 text-sm">
                  {props.userResponse.user.unfavoriteFoods
                    ? props.userResponse.user.unfavoriteFoods.length !== 0
                      ? props.userResponse.user.unfavoriteFoods
                      : '登録がありません'
                    : '登録がありません'}
                </div>
                {props.isMe && (
                  <Link href="/account/profile">
                    <div className="flex items-center justify-center flex-shrink-0 w-6 h-6 rounded-full bg-function-cv2">
                      <PlusIcon className="w-2 h-2 fill-text" />
                    </div>
                  </Link>
                )}
              </div>
            </div>
            <div>
              <div className="text-sm font-bold">アレルギー</div>
              <div className="flex items-end justify-between">
                <div className="mt-1 text-sm">
                  {props.userResponse.user.allergicFoods
                    ? props.userResponse.user.allergicFoods.length !== 0
                      ? props.userResponse.user.allergicFoods
                      : '登録がありません'
                    : '登録がありません'}
                </div>
                {props.isMe && (
                  <Link href="/account/profile">
                    <div className="flex items-center justify-center flex-shrink-0 w-6 h-6 rounded-full bg-function-cv2">
                      <PlusIcon className="w-2 h-2 fill-text" />
                    </div>
                  </Link>
                )}
              </div>
            </div>
            <div>
              <div className="text-sm font-bold">飲酒量</div>
              <div className="flex items-end justify-between">
                <div className="mt-1 text-sm">
                  {props.userResponse.user.alcoholAmountType != null &&
                  props.userResponse.user.alcoholAmountType !== 'unselected'
                    ? userAlcoholAmountTypeLabel(
                        props.userResponse.user.alcoholAmountType
                      )
                    : '登録がありません'}
                </div>
                {props.isMe && (
                  <Link href="/account/profile">
                    <div className="flex items-center justify-center flex-shrink-0 w-6 h-6 rounded-full bg-function-cv2">
                      <PlusIcon className="w-2 h-2 fill-text" />
                    </div>
                  </Link>
                )}
              </div>
            </div>
            <div>
              <div className="text-sm font-bold">好きなお酒</div>
              <div className="flex items-end justify-between">
                <div className="mt-1 text-sm">
                  {props.userResponse.user.favoriteAlcohol != null &&
                  props.userResponse.user.favoriteAlcohol != ''
                    ? props.userResponse.user.favoriteAlcohol
                    : '登録がありません'}
                </div>
                {props.isMe && (
                  <Link href="/account/profile">
                    <div className="flex items-center justify-center flex-shrink-0 w-6 h-6 rounded-full bg-function-cv2">
                      <PlusIcon className="w-2 h-2 fill-text" />
                    </div>
                  </Link>
                )}
              </div>
            </div>
          </div>

          <div className="w-full border-b my-7 border-gray" />

          <ScheduledDinnerList
            isMe={props.isMe}
            user={props.userResponse.user}
          />
        </ContainerInset>
      </Container>
      <Footer />
    </>
  )
}

const IntroductionLettersSection: React.FC<{ user: User; isMe: boolean }> = (
  props
) => {
  const router = useRouter()
  const sentIntroductionLetter = useFoodSWRWithErrors(
    '/user/introduction_letter',
    {
      toUserId: props.user.userId,
    },
    {
      disabled: props.isMe,
      allowErrors: [404],
    }
  )

  const introductionLetters = useFoodSWR('/user/introduction_letters', {
    userId: props.user.userId,
    state: 'published',
    count: 5,
  })

  const isEmpty = useMemo(
    () => introductionLetters.data?.letters.length === 0,
    [introductionLetters.data?.letters.length]
  )

  const hasPending = useMemo(
    () =>
      props.isMe &&
      introductionLetters.data?.pendingCount != null &&
      introductionLetters.data.pendingCount > 0,
    [introductionLetters.data, props.isMe]
  )

  const isNotSent = useMemo(
    () =>
      !props.isMe &&
      !sentIntroductionLetter.isLoading &&
      sentIntroductionLetter.data == null,
    [props.isMe, sentIntroductionLetter.data, sentIntroductionLetter.isLoading]
  )

  const commentCount = useMemo(
    () =>
      introductionLetters.data?.paging.all !== undefined
        ? `（${introductionLetters.data?.paging.all}件）`
        : '',
    [introductionLetters.data?.paging.all]
  )

  return (
    <div className="mb-8 space-y-4">
      <div className="font-bold">紹介コメント{commentCount}</div>
      {(hasPending || isEmpty || isNotSent) && (
        <div className="bg-[#f5f5f5] rounded p-4 text-sm">
          {hasPending && (
            <>
              <div className="flex justify-between items-center">
                <span className="font-bold text-secondary">
                  承認待ちが
                  {introductionLetters.data?.pendingCount}
                  件あります
                </span>
                <Link
                  href="/account/introduction_letters/received"
                  className="rounded text-white bg-secondary text-sm px-2 py-1 font-bold"
                >
                  確認
                </Link>
              </div>
            </>
          )}
          {hasPending && isEmpty && <hr className="text-gray my-3" />}
          {isEmpty && (
            <>
              紹介コメントがありません。 <br />
              {props.isMe && (
                <>
                  友人に紹介コメントをお願いしましょう。
                  <br />
                  紹介コメントがあると、食事会に参加しやすくなります。
                </>
              )}
            </>
          )}
          {isNotSent && (
            <>
              みなさんに{props.user.displayName}
              さんがどんな人か紹介してみましょう!
              <div className="px-4 mt-3">
                <Link
                  href={`/foodies/${props.user.userName}/introduction_letters/new`}
                >
                  <Button variant="primary" className="mx-auto px-4">
                    紹介コメントを書く
                  </Button>
                </Link>
              </div>
            </>
          )}
        </div>
      )}

      {introductionLetters.data?.letters.map((letter) => {
        return (
          <div key={letter.introductionLetterId}>
            <IntroductionLetterListItem
              letter={letter}
              toUser={props.user}
              hideSentAt
              hideBodyText
            />
          </div>
        )
      })}

      {introductionLetters.data &&
        introductionLetters.data.letters.length <
          introductionLetters.data.paging.all && (
          <Link
            href={`/foodies/${router.query.user_name}/introduction_letters`}
          >
            <Button
              variant="functionWeak"
              type="button"
              className="mx-auto mt-5"
            >
              すべての紹介コメントを見る
            </Button>
          </Link>
        )}
    </div>
  )
}

export const getServerSideProps = withIronSessionSsr(
  // withIronSessionSsr をかませると、InferGetServerSideProps が効かなくなるので、
  // 型注釈を明示的に書く
  async (
    context: GetServerSidePropsContext
  ): Promise<
    | {
        props: {
          userResponse: UserResponse
          userMemoResponse: UserMemoResponse | undefined
          accessToken: string | undefined
          isMe: boolean
          visits: {
            id: number
            name: string
            image: string
          }[]
          titles: {
            id: number
            name: string
            image: string
          }[]
        }
      }
    | { redirect: { destination: string; permanent: boolean } }
  > => {
    invariant(typeof context.query.user_name === 'string')

    try {
      const userResponse = await userGetProfile({
        accessToken: context.req.session.user?.accessToken,
        searchParams: {
          user_name: context.query.user_name,
        },
      })

      const isMe =
        context.req.session.user?.userId != null &&
        context.req.session.user?.userId === userResponse.user.userId

      let userMemoResponse: UserMemoResponse | undefined = undefined
      if (!isMe && context.req.session.user != null) {
        userMemoResponse = await userUserMemo({
          accessToken: context.req.session.user.accessToken,
          searchParams: {
            user_name: context.query.user_name,
          },
        })
      }

      const props = {
        accessToken: context.req.session.user?.accessToken,
        isMe: isMe,
        userResponse: userResponse,
        userMemoResponse: userMemoResponse,
        // TODO: ここから下のデータは CSR (SWR) で取得するように変更する
        visits: context.query.empty
          ? []
          : [
              {
                id: 1,
                name: 'A',
                image: '/images/visits/comptoir-feu.svg',
              },
              {
                id: 2,
                name: 'A',
                image: '/images/visits/comptoir-feu.svg',
              },
              {
                id: 3,
                name: 'A',
                image: '/images/visits/comptoir-feu.svg',
              },
              {
                id: 4,
                name: 'A',
                image: '/images/visits/comptoir-feu.svg',
              },
              {
                id: 5,
                name: 'A',
                image: '/images/visits/comptoir-feu.svg',
              },
            ],
        titles: context.query.empty
          ? []
          : [
              {
                id: 1,
                name: 'A',
                image: '/images/titles/justice.svg',
              },
              {
                id: 2,
                name: 'A',
                image: '/images/titles/justice.svg',
              },
              {
                id: 3,
                name: 'A',
                image: '/images/titles/justice.svg',
              },
              {
                id: 4,
                name: 'A',
                image: '/images/titles/justice.svg',
              },
              {
                id: 5,
                name: 'A',
                image: '/images/titles/justice.svg',
              },
            ],
      }

      return { props }
    } catch (error) {
      if (isSuperFetchError(error) && error.response.status === 404) {
        return {
          redirect: {
            destination: '/dinners',
            permanent: false,
          },
        }
      }
      throw error
    }
  },
  sessionOptions
)

export default Page
